import { Text } from '@figma/fpl-components';
import { spacer } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { I18N_KEYS } from '@/language/keys';
import { Trans } from '@/UI/Trans/Trans';
import { ToolDescription } from './ToolDescription';
import type { ToolViewMetadata } from '@/api/toolView.api';

interface ToolMainViewHeaderProps {
  title: string;
  metadata?: ToolViewMetadata;
}

export function ToolMainViewHeader({ title, metadata }: ToolMainViewHeaderProps) {
  const labels = I18N_KEYS.TOOL_VIEW;
  const creator = metadata?.creator;

  return (
    <header data-testid="tool-main-view-header" {...stylex.props(styles.header)}>
      {metadata?.thumbnail && (
        <img
          src={metadata.thumbnail}
          alt=""
          onError={({ currentTarget }) => {
            currentTarget.style.display = 'none';
          }}
          {...stylex.props(styles.thumbnail)}
        />
      )}
      <div {...stylex.props(styles.metadata)}>
        <div {...stylex.props(styles.titleAndAuthor)}>
          <Text.h1 variant="heading-large" color="default" {...stylex.props(styles.title)}>
            <span title={title}>{title}</span>
          </Text.h1>
          {creator?.displayName && (
            <div {...stylex.props(styles.creator)}>
              {creator.photoURL && (
                <img
                  src={creator.photoURL}
                  alt=""
                  onError={({ currentTarget }) => {
                    currentTarget.style.display = 'none';
                  }}
                  {...stylex.props(styles.creatorPhoto)}
                />
              )}
              <Text.span variant="body-large" color="secondary" {...stylex.props(styles.creatorName)}>
                <Trans
                  i18nKey={labels.BY_AUTHOR}
                  values={{ author: creator.displayName }}
                  components={{ author: <Text.span variant="body-large" color="default" /> }}
                />
              </Text.span>
            </div>
          )}
        </div>
        {metadata?.description && (
          <ToolDescription key={metadata.description} description={metadata.description} />
        )}
      </div>
    </header>
  );
}

const styles = stylex.create({
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    flexShrink: 0,
    minWidth: 0,
    padding: spacer.spacer32px,
    gap: 30,
  },
  thumbnail: { width: 160, height: 160, flexShrink: 0, objectFit: 'cover', borderRadius: 5 },
  metadata: { display: 'flex', flexDirection: 'column', minWidth: 0, gap: spacer.spacer12px },
  titleAndAuthor: { display: 'flex', flexDirection: 'column', minWidth: 0, gap: spacer.spacer4px },
  creator: { display: 'flex', alignItems: 'center', minWidth: 0, gap: spacer.spacer4px },
  creatorPhoto: { width: 16, height: 16, flexShrink: 0, objectFit: 'cover', borderRadius: '50%' },
  title: {
    margin: 0,
    overflowWrap: 'anywhere',
  },
  creatorName: { minWidth: 0, overflowWrap: 'anywhere' },
});
